import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';

// Modal de confirmación inmutable: el patrón único para toda acción que
// deja rastro permanente (dar de baja, cambiar un rol, suspender a alguien).
//
// Tres cosas lo distinguen de un confirm() del navegador:
//   1. Enseña el cambio EXACTO que va a ocurrir ("ENCARGADO → OPERADOR"),
//      para que nadie apruebe a ciegas.
//   2. Puede exigir un motivo escrito, que viaja al backend y queda en la
//      auditoría. Sin motivo el botón no se activa.
//   3. Para lo destructivo puede exigir teclear una palabra, de modo que
//      no se dispare por reflejo.
//
// props:
//   titulo, descripcion, aviso?          texto explicativo
//   cambios?: [{ campo, antes, despues }] tabla del antes/después
//   extra?: JSX                          campos propios de la acción
//   pedirMotivo?: bool                   exige motivo (mín. 3 caracteres)
//   palabraConfirmacion?: string         exige teclearla tal cual
//   etiquetaConfirmar, peligro?
//   onConfirmar: (motivo) => Promise     onCerrar: () => void
export default function ModalConfirmar({
  titulo,
  descripcion,
  aviso,
  cambios,
  extra,
  pedirMotivo = false,
  palabraConfirmacion,
  etiquetaConfirmar = 'Confirmar',
  peligro = false,
  onConfirmar,
  onCerrar,
  // Por omisión NO: un motivo a medio escribir no es trabajo que guardar.
  // Lo enciende quien mete un formulario de verdad en `extra` (la recepción).
  guardia = false,
  // Lo que todavía impide confirmar y que el formulario de `extra` sabe
  // (U-02 fase b: «corrige el código…»). Opcional: sin él, igual que antes.
  bloqueo = '',
}) {
  const [motivo, setMotivo] = useState('');
  const [palabra, setPalabra] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const motivoListo = !pedirMotivo || motivo.trim().length >= 3;
  const palabraLista = !palabraConfirmacion || palabra.trim().toUpperCase() === palabraConfirmacion.toUpperCase();
  const listo = motivoListo && palabraLista && !bloqueo && !enviando;
  // QUÉ FALTA, dicho al lado del botón (Doctrina 2, regla 8). Un botón gris
  // sin explicación fue lo que midió la auditoría del 19 sep 2026 en la
  // baja de producto: quien no había leído el campo de arriba no sabía
  // por qué no podía seguir.
  const falta = enviando ? '' : [
    !motivoListo && 'escribe el motivo (mínimo 3 letras)',
    !palabraLista && `escribe ${palabraConfirmacion} para confirmar`,
    bloqueo,
  ].filter(Boolean).join(' y ');

  async function confirmar() {
    setError(null);
    setEnviando(true);
    try {
      await onConfirmar(motivo.trim());
      onCerrar();
    } catch (e) {
      setError(e.message || 'No se pudo completar la acción.');
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={titulo}
      icono={peligro ? AlertTriangle : undefined}
      onCerrar={onCerrar}
      // Mientras se aplica no se puede abandonar: cerrar a media escritura
      // dejaria al usuario sin saber si el cambio se hizo o no.
      bloqueado={enviando}
      // Un motivo a medio escribir no es trabajo que guardar: «atrás»
      // cierra la confirmación sin preguntar, igual que «Cancelar».
      guardia={guardia}
      pie={
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 flex-wrap">
          {falta && <p id="modal-confirmar-falta" role="note" className="text-xs text-gray-500 mr-auto">Para seguir, {falta}.</p>}
          <button
            type="button"
            onClick={onCerrar}
            disabled={enviando}
            className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={!listo}
            aria-describedby={falta ? 'modal-confirmar-falta' : undefined}
            className={`text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50 ${
              peligro ? 'bg-red-600 hover:bg-red-700' : 'bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)]'
            }`}
          >
            {enviando ? 'Aplicando…' : etiquetaConfirmar}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
          {descripcion && <p className="text-sm text-gray-600">{descripcion}</p>}

          {/* Los campos propios van ANTES del antes/después, porque son los
              que lo determinan: elegir el rol nuevo cambia lo que se ve abajo. */}
          {extra}

          {cambios?.length > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {cambios.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="text-gray-500">{c.campo}</span>
                  <span className="font-medium text-gray-900 text-right">
                    <span className="text-gray-500 line-through">{c.antes ?? '—'}</span>
                    <span className="mx-1.5 text-gray-500">→</span>
                    {c.despues ?? '—'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {aviso && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{aviso}</div>
          )}

          {pedirMotivo && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Motivo (obligatorio)</span>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                autoFocus
                placeholder="Por qué se hace este cambio…"
                className="entrada"
              />
              <span className="block text-xs text-gray-500 mt-1">Queda guardado en la auditoría junto con tu nombre y la fecha.</span>
            </label>
          )}

          {palabraConfirmacion && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Escribe <span className="font-mono">{palabraConfirmacion}</span> para confirmar
              </span>
              <input value={palabra} onChange={(e) => setPalabra(e.target.value)} className="entrada font-mono" />
            </label>
          )}

          {/* `alert` y no un párrafo cualquiera: el error aparece DESPUÉS
              de pulsar, cuando el foco ya está en el botón, y sin esto un
              lector de pantalla no lo menciona nunca. */}
          {error && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
              {error}
            </div>
          )}
      </div>
    </Modal>
  );
}
